<script setup lang="ts">
import { ElConfigProvider } from 'element-plus'
import { elementLocales } from '@/i18n/element-locales'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { Leaf, Moon, Sun } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import AppSidebar from '@/components/AppSidebar.vue'
import AppWordmark from '@/components/AppWordmark.vue'
import LanguageSelect from '@/components/LanguageSelect.vue'
import GameIcon from '@/components/GameIcon.vue'
import OcrStartupGate from '@/components/OcrStartupGate.vue'
import GameTypeChoiceDialog from '@/components/GameTypeChoiceDialog.vue'
import FirstUseCalibrationGate from '@/components/FirstUseCalibrationGate.vue'
import SessionStartIssueDialog from '@/components/SessionStartIssueDialog.vue'
import UpdateAnnouncementDialog from '@/components/UpdateAnnouncementDialog.vue'
import AppUpdatePrompt from '@/components/AppUpdatePrompt.vue'
import BuffTourGate from '@/components/buffs/BuffTourGate.vue'
import { appUpdates } from '@/services/update-runtime'
import { reloadSafety } from '@/services/reload-safety'
import { useAppStore } from '@/stores/app'
import { useLiveStore } from '@/stores/live'
import { useBuffStore } from '@/stores/buffs'
import { GAME_ICONS } from '@/ui/game-icons'

const { t } = useI18n()
const app = useAppStore()
const live = useLiveStore()
const buffs = useBuffStore()
const route = useRoute()
const isSecurityReport = computed(() => route.name === 'security')
const initialized = ref(false)
const startupDialogOccupied = ref(false)
const firstUseDialogOccupied = ref(false)
const gameTypeDialogOccupied = ref(false)
const startIssueDialogOccupied = ref(false)
const announcementOccupied = ref(true)
const buffTourOpen = ref(false)
const announcementBlocked = computed(() => reloadSafety.preparing || buffs.requested || startupDialogOccupied.value || gameTypeDialogOccupied.value || startIssueDialogOccupied.value
  || live.firstUseCalibrationBlocking || firstUseDialogOccupied.value
  || live.isSelectingWindow || live.isStarting || live.isEnding
  || Boolean(live.session && live.session.status !== 'ended'))
const tourBlocked = computed(() => reloadSafety.preparing || startupDialogOccupied.value || gameTypeDialogOccupied.value || startIssueDialogOccupied.value
  || live.firstUseCalibrationBlocking || firstUseDialogOccupied.value
  || live.isSelectingWindow || live.isStarting || live.isEnding
  || (!buffTourOpen.value && announcementOccupied.value))
let publicAppInitialized = false

function initializeAfterFirstPaint(): void {
  if (publicAppInitialized || isSecurityReport.value) return
  publicAppInitialized = true
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      void live.initialize().finally(() => { initialized.value = true; appUpdates.state.ready = true })
      void buffs.initialize().catch(() => undefined)
    })
  })
}

function preparePageEnter(element: Element): void {
  element.removeAttribute('inert')
}

function preparePageLeave(element: Element): void {
  element.setAttribute('inert', '')
}

function scrollToRouteAnchor(): void {
  if (!isSecurityReport.value && (startupDialogOccupied.value || gameTypeDialogOccupied.value || firstUseDialogOccupied.value || live.firstUseCalibrationBlocking)) return
  const target = route.hash ? document.getElementById(route.hash.slice(1)) : null
  if (!target) return
  target.scrollIntoView({ block: 'start', behavior: 'auto' })
  target.focus({ preventScroll: true })
}

onMounted(initializeAfterFirstPaint)
watch(isSecurityReport, report => {
  if (report) { startupDialogOccupied.value = false; gameTypeDialogOccupied.value = false; firstUseDialogOccupied.value = false; startIssueDialogOccupied.value = false }
  else initializeAfterFirstPaint()
})
watch(() => route.fullPath, async () => {
  await nextTick()
  if (!isSecurityReport.value && (startupDialogOccupied.value || gameTypeDialogOccupied.value || firstUseDialogOccupied.value || live.firstUseCalibrationBlocking)) return
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true })
  scrollToRouteAnchor()
})
</script>

<template>
  <ElConfigProvider :locale="elementLocales[app.locale]">
  <OcrStartupGate v-if="!isSecurityReport" @visibility-change="startupDialogOccupied = $event" />
  <GameTypeChoiceDialog v-if="!isSecurityReport" :startup-occupied="startupDialogOccupied" @visibility-change="gameTypeDialogOccupied = $event" />
  <FirstUseCalibrationGate v-if="!isSecurityReport" :startup-occupied="startupDialogOccupied || gameTypeDialogOccupied || Boolean(live.gameTypeChoice)" @visibility-change="firstUseDialogOccupied = $event" />
  <SessionStartIssueDialog v-if="!isSecurityReport && !live.gameTypeChoiceOpen" @visibility-change="startIssueDialogOccupied = $event" />
  <UpdateAnnouncementDialog :ready="initialized" :blocked="isSecurityReport || announcementBlocked || buffTourOpen" @occupancy-change="announcementOccupied = $event" />
  <BuffTourGate :ready="initialized" :blocked="tourBlocked" @visibility-change="buffTourOpen = $event" />
  <AppUpdatePrompt v-if="!isSecurityReport" />
  <div class="app-shell" :inert="reloadSafety.preparing || undefined">
    <AppSidebar />
    <header class="mobile-topbar">
      <RouterLink class="mobile-brand" to="/" :aria-label="t('app.name')">
        <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.75rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
        <AppWordmark />
      </RouterLink>
      <div class="mobile-topbar-actions">
        <LanguageSelect class="topbar-language" />
        <button class="icon-btn topbar-theme" type="button" :title="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
          <Moon v-if="app.theme === 'light'" style="width:1.125rem;height:1.125rem" aria-hidden="true" />
          <Sun v-else style="width:1.125rem;height:1.125rem" aria-hidden="true" />
        </button>
      </div>
    </header>
    <main id="main-content" class="app-main" tabindex="-1">
      <RouterView v-slot="{ Component, route: pageRoute }">
        <div class="route-stage">
          <Transition name="page" appear @before-enter="preparePageEnter" @after-enter="scrollToRouteAnchor" @before-leave="preparePageLeave" @leave-cancelled="preparePageEnter">
            <div v-if="Component" :key="pageRoute.path" class="route-page" :class="{ 'route-page--rank': pageRoute.name === 'rank' }">
              <component :is="Component" />
            </div>
          </Transition>
        </div>
      </RouterView>
    </main>
  </div>
  </ElConfigProvider>
</template>
